import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Checkbox,
  Chip,
  Collapse,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from '@mui/material';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { formatApiErrorMessage } from '../../utils/apiErrorMessage';
import { fetchGatewayExeTargets, type GatewayExePool } from '../../services/gatewayExeUpdateService';
import {
  createGatewayRemote,
  endGatewayRemote,
  GATEWAY_REMOTE_FOLDERS,
  gatewayRemoteFolderLabel,
  gatewayRemoteViewUrl,
  setGatewayRemoteTalk,
  uploadGatewayRemoteFile,
  type GatewayRemoteFolder,
  type GatewayRemoteProduct,
} from '../../services/gatewayRemoteService';
import { RemoteTalk, talkDirections, type TalkFlags } from '../../services/gatewayRemoteAudio';

const CODE_VK: Record<string, number> = {
  Backspace: 8,
  Tab: 9,
  Enter: 13,
  ShiftLeft: 16,
  ShiftRight: 16,
  ControlLeft: 17,
  ControlRight: 17,
  AltLeft: 18,
  AltRight: 18,
  Pause: 19,
  CapsLock: 20,
  Escape: 27,
  Space: 32,
  PageUp: 33,
  PageDown: 34,
  End: 35,
  Home: 36,
  ArrowLeft: 37,
  ArrowUp: 38,
  ArrowRight: 39,
  ArrowDown: 40,
  Insert: 45,
  Delete: 46,
  MetaLeft: 91,
  MetaRight: 92,
  ContextMenu: 93,
  Numpad0: 96,
  Numpad1: 97,
  Numpad2: 98,
  Numpad3: 99,
  Numpad4: 100,
  Numpad5: 101,
  Numpad6: 102,
  Numpad7: 103,
  Numpad8: 104,
  Numpad9: 105,
  NumpadMultiply: 106,
  NumpadAdd: 107,
  NumpadSubtract: 109,
  NumpadDecimal: 110,
  NumpadDivide: 111,
  Semicolon: 186,
  Equal: 187,
  Comma: 188,
  Minus: 189,
  Period: 190,
  Slash: 191,
  Backquote: 192,
  BracketLeft: 219,
  Backslash: 220,
  BracketRight: 221,
  Quote: 222,
};

function codeToVk(code: string): number {
  const known = CODE_VK[code];
  if (known) return known;
  if (/^Key[A-Z]$/.test(code)) return code.charCodeAt(3);
  if (/^Digit[0-9]$/.test(code)) return code.charCodeAt(5);
  const fn = /^F(\d{1,2})$/.exec(code);
  if (fn) {
    const n = Number(fn[1]);
    if (n >= 1 && n <= 12) return 111 + n;
  }
  return 0;
}

function imageNorm(img: HTMLImageElement, clientX: number, clientY: number): { x: number; y: number } | null {
  const rect = img.getBoundingClientRect();
  const nw = img.naturalWidth;
  const nh = img.naturalHeight;
  if (!nw || !nh || rect.width < 1 || rect.height < 1) return null;
  const scale = Math.min(rect.width / nw, rect.height / nh);
  const dw = nw * scale;
  const dh = nh * scale;
  const ox = rect.left + (rect.width - dw) / 2;
  const oy = rect.top + (rect.height - dh) / 2;
  const x = (clientX - ox) / dw;
  const y = (clientY - oy) / dh;
  if (x < 0 || y < 0 || x > 1 || y > 1) return null;
  return { x, y };
}

const GatewayRemoteCard: React.FC = () => {
  const [product, setProduct] = useState<GatewayRemoteProduct>('data_gateway');
  const [pools, setPools] = useState<GatewayExePool[]>([]);
  const [controlRoomOnline, setControlRoomOnline] = useState(false);
  const [poolId, setPoolId] = useState<number | ''>('');
  const [error, setError] = useState<string | null>(null);
  const [phase, setPhase] = useState<'idle' | 'connecting' | 'live' | 'ended' | 'failed'>('idle');
  const [frameUrl, setFrameUrl] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const [popout, setPopout] = useState(false);
  const [boxText, setBoxText] = useState('');
  const [waitSec, setWaitSec] = useState(0);
  const [flags, setFlags] = useState<TalkFlags>({
    localMic: false,
    localSpeaker: false,
    remoteMic: false,
    remoteSpeaker: false,
  });
  const [talking, setTalking] = useState(false);
  const [sendFile, setSendFile] = useState<File | null>(null);
  const [sendFolder, setSendFolder] = useState<GatewayRemoteFolder>('desktop');
  const [sendingFile, setSendingFile] = useState(false);
  const [fileNote, setFileNote] = useState<string | null>(null);
  const [fileSeverity, setFileSeverity] = useState<'info' | 'success' | 'warning' | 'error'>('info');
  const fileWaitRef = useRef(0);
  const wsRef = useRef<WebSocket | null>(null);
  const sessionRef = useRef<string | null>(null);
  const imgRef = useRef<HTMLImageElement | null>(null);
  const frameUrlRef = useRef<string | null>(null);
  const phaseRef = useRef(phase);
  const talkRef = useRef<RemoteTalk | null>(null);
  const talkingRef = useRef(false);
  const heldKeys = useRef<Set<number>>(new Set());
  const heldButtons = useRef<Set<number>>(new Set());
  const lastPoint = useRef({ x: 0.5, y: 0.5 });
  const lastMove = useRef(0);
  const waitTimer = useRef<number | null>(null);

  useEffect(() => {
    phaseRef.current = phase;
  }, [phase]);

  useEffect(() => {
    if (phase !== 'connecting') return;
    const started = Date.now();
    setWaitSec(0);
    const id = window.setInterval(() => {
      setWaitSec(Math.floor((Date.now() - started) / 1000));
    }, 500);
    return () => window.clearInterval(id);
  }, [phase]);

  const load = useCallback(async () => {
    try {
      const targets = await fetchGatewayExeTargets();
      setPools(targets.pools || []);
      setControlRoomOnline(Boolean(targets.control_room_online));
    } catch (e: unknown) {
      setError(formatApiErrorMessage(e, '대상 목록을 불러오지 못함'));
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const send = useCallback((body: Record<string, unknown>) => {
    const ws = wsRef.current;
    if (!ws || ws.readyState !== WebSocket.OPEN) return;
    ws.send(JSON.stringify(body));
  }, []);

  const releaseButtons = useCallback(() => {
    const x = lastPoint.current.x;
    const y = lastPoint.current.y;
    heldButtons.current.forEach((button) => {
      send({ t: 'mouse', a: 'up', b: button, x, y });
    });
    heldButtons.current.clear();
  }, [send]);

  const releaseKeys = useCallback(() => {
    heldKeys.current.forEach((vk) => {
      send({ t: 'key', a: 'up', vk });
    });
    heldKeys.current.clear();
  }, [send]);

  const detachLocal = useCallback(
    (opts?: { endRemote?: boolean }) => {
      if (waitTimer.current != null) {
        window.clearTimeout(waitTimer.current);
        waitTimer.current = null;
      }
      releaseButtons();
      releaseKeys();
      talkRef.current?.stop();
      talkRef.current = null;
      talkingRef.current = false;
      setTalking(false);
      const ws = wsRef.current;
      wsRef.current = null;
      if (ws) {
        ws.onclose = null;
        ws.close();
      }
      const sid = sessionRef.current;
      sessionRef.current = null;
      if (opts?.endRemote && sid) void endGatewayRemote(sid).catch(() => undefined);
      if (frameUrlRef.current) {
        URL.revokeObjectURL(frameUrlRef.current);
        frameUrlRef.current = null;
      }
      setFrameUrl(null);
      setBoxText('');
      phaseRef.current = 'idle';
      setPhase('idle');
    },
    [releaseButtons, releaseKeys],
  );

  /** Explicit disconnect ends the facility capture session. */
  const stop = useCallback(() => detachLocal({ endRemote: true }), [detachLocal]);

  const detachLocalRef = useRef(detachLocal);
  detachLocalRef.current = detachLocal;
  // Tab close: leave server session for other viewers
  useEffect(() => () => detachLocalRef.current({ endRemote: false }), []);

  const failConnect = useCallback((text: string) => {
    if (waitTimer.current != null) {
      window.clearTimeout(waitTimer.current);
      waitTimer.current = null;
    }
    const ws = wsRef.current;
    wsRef.current = null;
    if (ws) {
      ws.onclose = null;
      ws.onerror = null;
      ws.close();
    }
    sessionRef.current = null;
    // Keep facility session alive for other viewers / retry
    phaseRef.current = 'failed';
    setBoxText(text);
    setError(text);
    setPhase('failed');
  }, []);

  useEffect(() => {
    const img = imgRef.current;
    if (!img) return;
    const onWheel = (ev: WheelEvent) => {
      if (phaseRef.current !== 'live') return;
      ev.preventDefault();
      const point = imageNorm(img, ev.clientX, ev.clientY);
      if (!point) return;
      lastPoint.current = point;
      const dy = ev.deltaY > 0 ? 1 : ev.deltaY < 0 ? -1 : 0;
      if (!dy) return;
      const ws = wsRef.current;
      if (!ws || ws.readyState !== WebSocket.OPEN) return;
      ws.send(JSON.stringify({ t: 'mouse', a: 'wheel', x: point.x, y: point.y, dy }));
    };
    img.addEventListener('wheel', onWheel, { passive: false });
    return () => img.removeEventListener('wheel', onWheel);
  }, [frameUrl, popout]);

  useEffect(() => {
    if (!popout) return;
    const id = window.setTimeout(() => {
      imgRef.current?.parentElement?.focus();
    }, 80);
    return () => window.clearTimeout(id);
  }, [popout, frameUrl]);

  const connect = async () => {
    if (product === 'data_gateway' && poolId === '') {
      setError('시설을 선택');
      return;
    }
    // Do not end an existing remote session — join it from another window/path
    detachLocal({ endRemote: false });
    setError(null);
    setBoxText('');
    phaseRef.current = 'connecting';
    setPhase('connecting');
    if (waitTimer.current != null) window.clearTimeout(waitTimer.current);
    waitTimer.current = window.setTimeout(() => {
      if (phaseRef.current !== 'connecting') return;
      failConnect('게이트웨이 응답 없음. 원격 제어를 지원하는 exe인지 확인');
    }, 20000);
    try {
      const session = await createGatewayRemote({
        product,
        pool_id: product === 'data_gateway' ? Number(poolId) : null,
      });
      if (phaseRef.current !== 'connecting') {
        return;
      }
      sessionRef.current = session.id;
      const ws = new WebSocket(gatewayRemoteViewUrl(session.id, session.ticket));
      ws.binaryType = 'arraybuffer';
      wsRef.current = ws;
      ws.onmessage = (ev) => {
        void (async () => {
          if (typeof ev.data === 'string') {
            try {
              const msg = JSON.parse(ev.data) as {
                type?: string;
                state?: string;
                detail?: string;
                name?: string;
                ok?: boolean;
                folder?: string;
              };
              if (msg.type === 'status' && msg.state === 'failed') {
                const detail = (msg.detail || '').trim() || '원격 연결 실패';
                if (waitTimer.current != null) {
                  window.clearTimeout(waitTimer.current);
                  waitTimer.current = null;
                }
                phaseRef.current = 'failed';
                setBoxText(detail);
                setError(detail);
                setPhase('failed');
                return;
              }
              if (msg.type === 'status' && msg.state === 'ended') {
                talkRef.current?.stop();
                talkingRef.current = false;
                setTalking(false);
                if (phaseRef.current === 'connecting') {
                  if (waitTimer.current != null) {
                    window.clearTimeout(waitTimer.current);
                    waitTimer.current = null;
                  }
                  phaseRef.current = 'failed';
                  setBoxText('원격 연결이 끊김');
                  setError('원격 연결이 끊김');
                  setPhase('failed');
                  return;
                }
                setPhase('ended');
              } else if (msg.type === 'file') {
                fileWaitRef.current += 1;
                const where = gatewayRemoteFolderLabel(msg.folder);
                setFileSeverity(msg.ok ? 'success' : 'error');
                setFileNote(
                  msg.ok
                    ? `${msg.name || '파일'} 저장 완료. 상대 PC ${where}`
                    : `${msg.name || '파일'} 저장 실패. 상대 PC ${where}`,
                );
              }
            } catch {
              /* ignore */
            }
            return;
          }
          let bytes: Uint8Array | null = null;
          if (ev.data instanceof ArrayBuffer) bytes = new Uint8Array(ev.data);
          else if (ev.data instanceof Blob) bytes = new Uint8Array(await ev.data.arrayBuffer());
          if (!bytes) return;
          if (bytes.length >= 2 && bytes[0] === 0xff && bytes[1] === 0xd8) {
            const blob = new Blob([bytes], { type: 'image/jpeg' });
            const url = URL.createObjectURL(blob);
            if (frameUrlRef.current) URL.revokeObjectURL(frameUrlRef.current);
            frameUrlRef.current = url;
            if (waitTimer.current != null) {
              window.clearTimeout(waitTimer.current);
              waitTimer.current = null;
            }
            phaseRef.current = 'live';
            setFrameUrl(url);
            setPhase('live');
            return;
          }
          if (bytes.length > 1 && bytes[0] === 0xa1) {
            talkRef.current?.play(bytes.subarray(1));
          }
        })();
      };
      ws.onerror = () => {
        if (phaseRef.current === 'connecting') setError('원격 화면 연결 실패');
      };
      ws.onclose = () => {
        if (phaseRef.current === 'failed' || phaseRef.current === 'idle') return;
        if (phaseRef.current === 'connecting') {
          if (waitTimer.current != null) {
            window.clearTimeout(waitTimer.current);
            waitTimer.current = null;
          }
          phaseRef.current = 'failed';
          setBoxText('원격 화면 연결이 끊김');
          setError('원격 화면 연결이 끊김');
          setPhase('failed');
          return;
        }
        if (sessionRef.current) setPhase('ended');
      };
    } catch (e: unknown) {
      const phaseNow: string = phaseRef.current;
      if (phaseNow === 'failed') return;
      if (waitTimer.current != null) {
        window.clearTimeout(waitTimer.current);
        waitTimer.current = null;
      }
      phaseRef.current = 'idle';
      setPhase('idle');
      setError(formatApiErrorMessage(e, '연결 실패'));
    }
  };

  const onMouse = (action: 'move' | 'down' | 'up' | 'wheel', ev: React.MouseEvent | React.WheelEvent) => {
    const img = imgRef.current;
    if (!img || phaseRef.current !== 'live') return;
    const point = imageNorm(img, ev.clientX, ev.clientY);
    if (!point) return;
    lastPoint.current = point;
    if (action === 'move') {
      const now = Date.now();
      if (now - lastMove.current < 30) return;
      lastMove.current = now;
      send({ t: 'mouse', a: 'move', x: point.x, y: point.y });
      return;
    }
    if (action === 'wheel') {
      const wheel = ev as React.WheelEvent;
      const dy = wheel.deltaY > 0 ? 1 : wheel.deltaY < 0 ? -1 : 0;
      if (!dy) return;
      send({ t: 'mouse', a: 'wheel', x: point.x, y: point.y, dy });
      return;
    }
    const button = ev.button === 2 ? 1 : ev.button === 1 ? 2 : 0;
    if (action === 'down') heldButtons.current.add(button);
    else heldButtons.current.delete(button);
    send({ t: 'mouse', a: action, b: button, x: point.x, y: point.y });
  };

  const onKey = (ev: React.KeyboardEvent, down: boolean) => {
    if (phaseRef.current !== 'live') return;
    if (ev.repeat) return;
    if (ev.key === 'Process' || ev.key === 'Unidentified') return;
    const vk = codeToVk(ev.code);
    if (!vk) {
      if (down && ev.key.length === 1 && !ev.ctrlKey && !ev.altKey && !ev.metaKey) {
        send({ t: 'key', a: 'ch', ch: ev.key });
      }
      return;
    }
    ev.preventDefault();
    if (down) heldKeys.current.add(vk);
    else heldKeys.current.delete(vk);
    send({ t: 'key', a: down ? 'down' : 'up', vk });
  };

  const applyTalk = async (next: TalkFlags, enabled: boolean) => {
    const sid = sessionRef.current;
    const ws = wsRef.current;
    if (!sid || !ws) return;
    const dir = talkDirections(next);
    const turnOn = enabled && (dir.send || dir.hear);
    if (enabled && !turnOn) {
      setError('말할 쪽과 들을 쪽을 함께 선택');
    }
    await setGatewayRemoteTalk(sid, {
      enabled: turnOn,
      local_mic: next.localMic,
      local_speaker: next.localSpeaker,
      remote_mic: next.remoteMic,
      remote_speaker: next.remoteSpeaker,
    });
    if (!turnOn) {
      talkRef.current?.stop();
      talkRef.current = null;
      talkingRef.current = false;
      setTalking(false);
      return;
    }
    const talk = talkRef.current || new RemoteTalk();
    talkRef.current = talk;
    await talk.start(ws, next);
    talkingRef.current = true;
    setTalking(true);
  };

  const onTalk = async () => {
    if (phaseRef.current !== 'live') return;
    setError(null);
    try {
      if (talkingRef.current) {
        await applyTalk(flags, false);
        return;
      }
      await applyTalk(flags, true);
    } catch (e: unknown) {
      talkRef.current?.stop();
      talkRef.current = null;
      talkingRef.current = false;
      setTalking(false);
      const sid = sessionRef.current;
      if (sid) {
        void setGatewayRemoteTalk(sid, {
          enabled: false,
          local_mic: flags.localMic,
          local_speaker: flags.localSpeaker,
          remote_mic: flags.remoteMic,
          remote_speaker: flags.remoteSpeaker,
        }).catch(() => undefined);
      }
      setError(formatApiErrorMessage(e, '대화를 시작하지 못함'));
    }
  };

  const changeFlag = async (partial: Partial<TalkFlags>) => {
    const next = { ...flags, ...partial };
    setFlags(next);
    if (!talkingRef.current) return;
    try {
      await applyTalk(next, true);
    } catch (e: unknown) {
      talkRef.current?.stop();
      talkRef.current = null;
      talkingRef.current = false;
      setTalking(false);
      const sid = sessionRef.current;
      if (sid) {
        void setGatewayRemoteTalk(sid, {
          enabled: false,
          local_mic: next.localMic,
          local_speaker: next.localSpeaker,
          remote_mic: next.remoteMic,
          remote_speaker: next.remoteSpeaker,
        }).catch(() => undefined);
      }
      setError(formatApiErrorMessage(e, '음성 설정을 바꾸지 못함'));
    }
  };

  const onSendFile = async () => {
    const sid = sessionRef.current;
    if (!sid || !sendFile || phaseRef.current !== 'live') return;
    setSendingFile(true);
    setError(null);
    fileWaitRef.current += 1;
    const token = fileWaitRef.current;
    const where = gatewayRemoteFolderLabel(sendFolder);
    setFileSeverity('info');
    setFileNote(`${sendFile.name} 전송 중. 상대 PC ${where} 결과 대기`);
    try {
      const saved = await uploadGatewayRemoteFile(sid, sendFile, sendFolder);
      if (fileWaitRef.current !== token) return;
      const savedWhere = gatewayRemoteFolderLabel(saved.folder);
      setFileNote(`${saved.name || sendFile.name} 전송 중. 상대 PC ${savedWhere} 결과 대기`);
      window.setTimeout(() => {
        if (fileWaitRef.current !== token) return;
        setFileSeverity('warning');
        setFileNote(`${saved.name || sendFile.name} 상대 PC 응답 없음. ${savedWhere} 저장 여부를 확인할 수 없음`);
      }, 20000);
      setSendFile(null);
    } catch (e: unknown) {
      fileWaitRef.current += 1;
      setFileSeverity('error');
      setFileNote(null);
      setError(formatApiErrorMessage(e, '파일 전송 실패'));
    } finally {
      setSendingFile(false);
    }
  };

  const phaseLabel =
    phase === 'live'
      ? '연결됨'
      : phase === 'connecting'
        ? '응답 대기'
        : phase === 'failed'
          ? '실패'
          : phase === 'ended'
            ? '끊김'
            : '대기';

  const renderScreen = (large: boolean) => (
    <Box
      tabIndex={0}
      onKeyDown={(ev) => onKey(ev, true)}
      onKeyUp={(ev) => onKey(ev, false)}
      onBlur={releaseKeys}
      onContextMenu={(ev) => ev.preventDefault()}
      sx={{
        position: 'relative',
        flex: large ? 1 : undefined,
        width: '100%',
        height: large ? '100%' : undefined,
        minHeight: large ? 0 : 280,
        bgcolor: '#111',
        borderRadius: 1,
        outline: 'none',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        '&:focus': { boxShadow: 'inset 0 0 0 2px #90caf9' },
      }}
    >
      {frameUrl ? (
        <Box
          component="img"
          ref={imgRef}
          src={frameUrl}
          alt="원격 화면"
          draggable={false}
          onMouseMove={(ev) => onMouse('move', ev)}
          onMouseDown={(ev) => {
            (ev.currentTarget.parentElement as HTMLElement | null)?.focus();
            onMouse('down', ev);
          }}
          onMouseUp={(ev) => onMouse('up', ev)}
          onMouseLeave={releaseButtons}
          sx={{
            width: '100%',
            height: large ? '100%' : undefined,
            maxHeight: large ? 'none' : '70vh',
            objectFit: 'contain',
            cursor: 'crosshair',
          }}
        />
      ) : (
        <Typography sx={{ color: '#bbb', px: 2, textAlign: 'center' }}>
          {phase === 'connecting'
            ? `게이트웨이 응답 대기 ${waitSec}초`
            : phase === 'failed'
              ? boxText || '원격 연결 실패'
              : '연결 전'}
        </Typography>
      )}
    </Box>
  );

  return (
    <>
    <Card sx={{ mb: 3 }}>
      <CardHeader
        title="원격 제어"
        subheader="같은 세션 추가 연결 가능. 연결 끊기만 종료. JPEG 방식이라 WebRTC CCTV와 대역·CPU를 나눠 씀 — 원격 중 CCTV 끊김이 늘 수 있음"
        onClick={() => setOpen((value) => !value)}
        sx={{ cursor: 'pointer' }}
        action={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }} onClick={(ev) => ev.stopPropagation()}>
            <Chip
              size="small"
              color={phase === 'live' ? 'success' : phase === 'failed' ? 'error' : 'default'}
              label={phaseLabel}
            />
            <Button
              size="small"
              disabled={phase !== 'connecting' && phase !== 'live'}
              onClick={() => setPopout(true)}
            >
              크게 보기
            </Button>
            <IconButton size="small" aria-label={open ? '접기' : '펼치기'} onClick={() => setOpen((value) => !value)}>
              {open ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          </Box>
        }
      />
      <Collapse in={open}>
      <CardContent>
        {error ? (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        ) : null}
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center', mb: 2 }}>
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel id="remote-product">대상</InputLabel>
            <Select
              labelId="remote-product"
              label="대상"
              value={product}
              onChange={(e) => {
                setProduct(e.target.value as GatewayRemoteProduct);
                setPoolId('');
              }}
              disabled={phase === 'connecting' || phase === 'live'}
            >
              <MenuItem value="data_gateway">시설 DataGateway.exe</MenuItem>
              <MenuItem value="control_room">관제실 ControlRoomGateway.exe</MenuItem>
            </Select>
          </FormControl>
          {product === 'data_gateway' ? (
            <FormControl size="small" sx={{ minWidth: 220 }}>
              <InputLabel id="remote-pool">시설</InputLabel>
              <Select
                labelId="remote-pool"
                label="시설"
                value={poolId}
                onChange={(e) => setPoolId(e.target.value === '' ? '' : Number(e.target.value))}
                disabled={phase === 'connecting' || phase === 'live'}
              >
                <MenuItem value="">선택</MenuItem>
                {pools.map((pool) => (
                  <MenuItem key={pool.id} value={pool.id}>
                    {pool.name || pool.code || pool.id}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          ) : (
            <Chip
              size="small"
              color={controlRoomOnline ? 'success' : 'default'}
              label={controlRoomOnline ? '관제실 에이전트 온라인' : '관제실 에이전트 오프라인'}
            />
          )}
          {phase === 'connecting' || phase === 'live' ? (
            <Button variant="outlined" color="inherit" onClick={stop}>
              연결 끊기
            </Button>
          ) : (
            <Button variant="contained" onClick={() => void connect()}>
              연결
            </Button>
          )}
        </Box>
        {popout ? (
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            크게 보는 중
          </Typography>
        ) : (
          renderScreen(false)
        )}
        <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary' }}>
          화면을 클릭한 뒤 키보드 입력 전달. Ctrl+Alt+Del 은 전달되지 않음.
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center', mt: 2 }}>
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel id="remote-file-folder">저장 폴더</InputLabel>
            <Select
              labelId="remote-file-folder"
              label="저장 폴더"
              value={sendFolder}
              onChange={(e) => setSendFolder(e.target.value as GatewayRemoteFolder)}
              disabled={sendingFile}
            >
              {GATEWAY_REMOTE_FOLDERS.map((item) => (
                <MenuItem key={item.id} value={item.id}>
                  {item.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button variant="outlined" component="label" disabled={phase !== 'live' || sendingFile}>
            파일 선택
            <input
              hidden
              type="file"
              onChange={(e) => setSendFile(e.target.files?.[0] || null)}
            />
          </Button>
          <Typography variant="body2">{sendFile ? sendFile.name : '파일 없음'}</Typography>
          <Button variant="contained" disabled={phase !== 'live' || !sendFile || sendingFile} onClick={() => void onSendFile()}>
            {sendingFile ? '보내는 중…' : '파일 보내기'}
          </Button>
        </Box>
        <Typography variant="body2" sx={{ mt: 0.5, color: 'text.secondary' }}>
          드롭다운에서 고른 상대 PC 폴더에 저장
        </Typography>
        {fileNote ? (
          <Alert severity={fileSeverity} sx={{ mt: 1 }} onClose={() => setFileNote(null)}>
            {fileNote}
          </Alert>
        ) : null}
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center', mt: 2 }}>
          <FormControlLabel
            control={<Checkbox checked={flags.localMic} onChange={(e) => void changeFlag({ localMic: e.target.checked })} disabled={phase !== 'live'} />}
            label="접속 컴 마이크"
          />
          <FormControlLabel
            control={<Checkbox checked={flags.localSpeaker} onChange={(e) => void changeFlag({ localSpeaker: e.target.checked })} disabled={phase !== 'live'} />}
            label="접속 컴 스피커"
          />
          <FormControlLabel
            control={<Checkbox checked={flags.remoteMic} onChange={(e) => void changeFlag({ remoteMic: e.target.checked })} disabled={phase !== 'live'} />}
            label="상대 PC 마이크"
          />
          <FormControlLabel
            control={<Checkbox checked={flags.remoteSpeaker} onChange={(e) => void changeFlag({ remoteSpeaker: e.target.checked })} disabled={phase !== 'live'} />}
            label="상대 PC 스피커"
          />
          <Button variant="contained" disabled={phase !== 'live'} onClick={() => void onTalk()}>
            {talking ? '대화 끊기' : '대화하기'}
          </Button>
        </Box>
        <Typography variant="body2" sx={{ mt: 0.5, color: 'text.secondary' }}>
          체크 후 대화하기. 양쪽 스피커가 켜지면 울림이 날 수 있음.
        </Typography>
      </CardContent>
      </Collapse>
    </Card>
    <Dialog
      open={popout}
      onClose={() => setPopout(false)}
      maxWidth={false}
      PaperProps={{
        sx: { width: '96vw', height: '94vh', maxWidth: '96vw', m: 1, display: 'flex', flexDirection: 'column' },
      }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 1 }}>
        원격 화면
        <Button onClick={() => setPopout(false)}>되돌리기</Button>
      </DialogTitle>
      <DialogContent sx={{ display: 'flex', flex: 1, minHeight: 0, p: 1 }}>
        {popout ? renderScreen(true) : null}
      </DialogContent>
    </Dialog>
    </>
  );
};

export default GatewayRemoteCard;
